import { useState, useEffect, FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SeoJsonLd from '../../components/SeoJsonLd';

export default function Result() {
  const location = useLocation();
  const navigate = useNavigate();
  const { amount, reason, businessAddress, businessArea, businessType, restorationAgreement, contractExpiry } = location.state || {};
  const [todayConsultations, setTodayConsultations] = useState(0);
  const [showFixedCTA, setShowFixedCTA] = useState(false);
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [consultName, setConsultName] = useState('');
  const [consultPhone, setConsultPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // 페이지 로드 시 최상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 스크롤 감지하여 고정 CTA 표시/숨김
  useEffect(() => {
    const handleScroll = () => {
      // 300px 이상 스크롤하면 고정 CTA 표시
      if (window.scrollY > 300) {
        setShowFixedCTA(true);
      } else {
        setShowFixedCTA(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 오늘 날짜의 시작 숫자를 가져오는 함수
  const getTodayStartNumber = () => {
    const today = new Date();
    const dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    
    let hash = 0;
    for (let i = 0; i < dateString.length; i++) {
      const char = dateString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    const randomStart = 50 + (Math.abs(hash) % 51);
    return randomStart;
  };

  // 오늘 상담요청 수 계산
  useEffect(() => {
    const startNumber = getTodayStartNumber();
    
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const minutesSinceMidnight = Math.floor((now.getTime() - midnight.getTime()) / (1000 * 60));
    
    const maxIncrease = 30 + (Math.abs(getTodayStartNumber()) % 51);
    const currentIncrease = Math.floor((minutesSinceMidnight / 1440) * maxIncrease);
    const currentTodayUsers = startNumber + currentIncrease;
    
    // 오늘 방문자 수의 2/3를 상담요청 수로 설정
    const consultations = Math.floor((currentTodayUsers * 2) / 3);
    setTodayConsultations(consultations);

    // 1분마다 카운트 증가
    const interval = setInterval(() => {
      setTodayConsultations(prev => prev + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const formatAmount = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  // 카카오톡 공유하기
  const shareKakao = () => {
    setShowShareMenu(false);
    if (typeof window.Kakao === 'undefined') {
      alert('카카오톡 공유 기능을 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init('YOUR_KAKAO_APP_KEY'); // 실제 카카오 앱 키로 교체 필요
    }

    const shareUrl = window.location.href;
    const shareTitle = amount > 0 
      ? `폐업지원금 ${formatAmount(amount)}원 받을 수 있어요!`
      : '폐업지원금 계산 결과를 확인하세요';
    const shareDescription = '7가지 정보만 입력하면 1분이내 확인가능';

    window.Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: shareTitle,
        description: shareDescription,
        imageUrl: 'https://public.readdy.ai/ai/img_res/fb677ddb7438918e73fd1fa49039b48f.jpg',
        link: {
          mobileWebUrl: shareUrl,
          webUrl: shareUrl,
        },
      },
      buttons: [
        {
          title: '결과 확인하기',
          link: {
            mobileWebUrl: shareUrl,
            webUrl: shareUrl,
          },
        },
      ],
    });
  };

  // 링크 복사하기
  const copyLink = async () => {
    setShowShareMenu(false);
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowCopyAlert(true);
      setTimeout(() => setShowCopyAlert(false), 2000);
    } catch (err) {
      alert('링크 복사에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 위험도 계산 함수
  const calculateRisk = () => {
    let riskScore = 0;
    const riskReasons: string[] = [];

    // 계약 종료 임박도 (최대 40점)
    if (contractExpiry === '이미 만료됨') {
      riskScore += 40;
      riskReasons.push('계약이 이미 만료되어 즉시 원상복구가 필요합니다.');
    } else if (contractExpiry === '7일 이내') {
      riskScore += 35;
      riskReasons.push('계약 종료가 7일 이내로 매우 임박했습니다.');
    } else if (contractExpiry === '15일 이내') {
      riskScore += 30;
      riskReasons.push('계약 종료가 15일 이내로 임박했습니다.');
    } else if (contractExpiry === '30일 이내') {
      riskScore += 20;
      riskReasons.push('계약 종료가 30일 이내로 임박했습니다.');
    } else if (contractExpiry === '90일 이내') {
      riskScore += 10;
      riskReasons.push('계약 종료가 90일 이내로 예정되어 있습니다.');
    }

    // 원상복구 범위 확정 여부 (최대 35점)
    if (restorationAgreement === '아직 안함') {
      riskScore += 35;
      riskReasons.push('임대인과 원상복구 범위가 확정되지 않아 보증금 공제 위험이 큽니다.');
    } else if (restorationAgreement === '모름') {
      riskScore += 25;
      riskReasons.push('원상복구 범위를 모르는 상태로 보증금 공제 위험이 있습니다.');
    } else if (restorationAgreement === '협의 중') {
      riskScore += 20;
      riskReasons.push('원상복구 범위 협의가 진행 중이므로 빠른 확정이 필요합니다.');
    }

    // 철거/원복 진행 여부 (최대 10점)
    if (location.state?.hasDemolitionStarted === '아니오') {
      riskScore += 10;
      riskReasons.push('철거/원복을 아직 시작하지 않았습니다.');
    } else if (location.state?.hasDemolitionStarted === '예') {
      riskReasons.push('철거를 이미 시작하여 원상복구 범위 협의가 어려울 수 있습니다.');
    }

    // 위험도 레벨 결정
    let riskLevel: '매우 높음' | '높음' | '낮음';
    let riskColor: string;
    let riskIcon: string;
    let riskConclusion: string;
    let damageAmount: string;
    let damageMessage: string;

    if (riskScore >= 65) {
      riskLevel = '매우 높음';
      riskColor = 'text-red-600';
      riskIcon = 'ri-error-warning-fill';
      riskConclusion = '지금 상태면 보증금을 \'덜 돌려받을\' 가능성이 큽니다. 폐업지원센터 체크리스트로 손해 나는 항목부터 확인하세요.';
      damageAmount = '200만원 ~ 500만원';
      damageMessage = '이 상태에서 원상복구 협의가 늦어지면 추가비용이 발생할 수 있어요.';
    } else if (riskScore >= 30) {
      riskLevel = '높음';
      riskColor = 'text-orange-600';
      riskIcon = 'ri-alert-fill';
      riskConclusion = '아직 늦지 않았습니다. 원상복구 범위만 확정해도 보증금 손실을 크게 줄일 수 있어요.';
      damageAmount = '50만원 ~ 200만원';
      damageMessage = '원상복구 범위 협의가 늦어지면 추가비용이 발생할 수 있어요.';
    } else {
      riskLevel = '낮음';
      riskColor = 'text-green-600';
      riskIcon = 'ri-checkbox-circle-fill';
      riskConclusion = '시간 여유가 있어 비용을 줄이고 보증금 손실을 예방하기 좋은 상태입니다. 폐업지원센터에서 절감 가능한 공정부터 확인하세요.';
      damageAmount = '절감 가능';
      damageMessage = '지금 준비하면 원상복구 비용을 30~50% 절감할 수 있어요.';
    }

    return {
      score: riskScore,
      level: riskLevel,
      color: riskColor,
      icon: riskIcon,
      conclusion: riskConclusion,
      reasons: riskReasons,
      damageAmount,
      damageMessage
    };
  };

  const riskAssessment = calculateRisk();

  // 상담 요청 폼 제출
  const handleConsultSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!consultName.trim()) {
      setSubmitError('성함을 입력해주세요.');
      return;
    }
    if (!consultPhone.trim()) {
      setSubmitError('연락처를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new URLSearchParams();
      formData.append('name', consultName);
      formData.append('phone', consultPhone);
      
      // 앞에서 선택한 항목들 추가
      if (businessAddress) formData.append('businessAddress', businessAddress);
      if (businessArea) formData.append('businessArea', businessArea);
      
      // 철거 진행 여부 변환: "예" → "철거 했음", "아니오" → "철거필요함"
      if (location.state?.hasDemolitionStarted) {
        const demolitionStatus = location.state.hasDemolitionStarted === '예' ? '철거 했음' : '철거필요함';
        formData.append('hasDemolitionStarted', demolitionStatus);
      }
      
      if (amount !== undefined) formData.append('estimatedAmount', `${formatAmount(amount)}원`);
      if (businessType) formData.append('businessType', businessType);

      const response = await fetch('https://readdy.ai/api/form/d60aaa83qj6k6vba3n5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setConsultName('');
        setConsultPhone('');
      } else {
        setSubmitError('제출에 실패했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      setSubmitError('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SeoJsonLd 
        type="WebPage" 
        data={{
          name: '폐업지원금 계산 결과',
          description: '폐업지원금 예상 수령금액 확인 및 무료 상담 신청'
        }}
      />
      <div className="min-h-screen bg-white overflow-x-hidden">
        {/* 카카오톡 SDK 로드 */}
        <script src="https://developers.kakao.com/sdk/js/kakao.js" async></script>

        {/* 링크 복사 알림 */}
        {showCopyAlert && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-xl"></i>
              <span className="text-sm font-medium">링크가 복사되었습니다!</span>
            </div>
          </div>
        )}

        <style>{`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
          @keyframes fade-in {
            from {
              opacity: 0;
              transform: translate(-50%, -10px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }
          .animate-gradient {
            background-size: 200% 200%;
            animation: gradientShift 3s ease-in-out infinite, pulse 2s ease-in-out infinite;
          }
          .animate-fade-in {
            animation: fade-in 0.3s ease-out;
          }
        `}</style>

        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 md:gap-4">
                {/* 로고: 계산기 아이콘 + 텍스트 */}
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-teal-600 rounded-full">
                    <i className="ri-calculator-line text-white text-xl md:text-2xl"></i>
                  </div>
                  <span className="text-gray-900 font-bold text-base md:text-lg whitespace-nowrap">폐업지원금 간편 계산기</span>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                {/* 공유 버튼 (통합) */}
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 text-sm md:text-base font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                    title="공유하기"
                  >
                    <i className="ri-share-line text-lg"></i>
                    <span>공유</span>
                  </button>

                  {/* 공유 메뉴 드롭다운 */}
                  {showShareMenu && (
                    <>
                      {/* 배경 클릭 시 닫기 */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowShareMenu(false)}
                      ></div>
                      
                      {/* 메뉴 */}
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                        <button
                          onClick={shareKakao}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-kakao-talk-fill text-xl text-yellow-500"></i>
                          <span>카카오톡 공유</span>
                        </button>
                        <button
                          onClick={copyLink}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-link text-xl text-gray-600"></i>
                          <span>링크 복사</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <button
                  onClick={() => navigate('/')}
                  className="px-3 md:px-4 py-2 text-xs md:text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  <span className="hidden sm:inline">다시 계산</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Result Section */}
        <section className="pt-6 md:pt-8 pb-3 md:pb-4 bg-gradient-to-br from-teal-50 to-cyan-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 md:mb-8">
              <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-600 rounded-full mx-auto mb-4 md:mb-6">
                <i className="ri-check-line text-white text-xl md:text-2xl"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
                폐업지원센터 진단 결과
              </h2>
            </div>

            {/* 카드1: 예상 지원금 결과 */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-10 mb-6 md:mb-8">
              {amount > 0 ? (
                <div className="text-center">
                  <p className="text-base md:text-lg text-gray-600 mb-2 md:mb-3">폐업지원금 예상 수령금액</p>
                  <p className="text-3xl md:text-5xl font-bold text-teal-600 mb-2">
                    {formatAmount(amount)}원
                  </p>
                  <p className="text-xs md:text-sm text-gray-500">
                    (면적 {businessArea} 기준 / 입력 정보 기반 추정치)
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4 md:mb-6">
                    <i className="ri-close-line text-red-600 text-2xl md:text-3xl"></i>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
                    지원금 수령 대상이<br className="md:hidden" /> 아닙니다
                  </p>
                  <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
                    사유: {reason}
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 md:p-6">
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                      입력하신 정보에 따르면 현재<br className="md:hidden" /> 폐업지원금 수령 대상에<br className="md:hidden" /> 해당하지 않습니다. 
                      자세한 상담을 원하시면<br className="md:hidden" /> 아래 견적 요청을 통해 문의해주세요.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* 추가 정부자금 카드 (위로 이동) */}
            {amount > 0 && (
              <div className="relative overflow-hidden rounded-2xl shadow-xl border border-gray-200 p-6 md:p-10 mb-6 md:mb-8">
                {/* 애니메이션 그라데이션 배경 */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 bg-[length:200%_100%] animate-gradient-flow"></div>
                <div className="absolute inset-0 bg-black/10"></div>
                
                <div className="relative z-10 text-center mb-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight">
                    지원금 상담 및 신청하기
                  </h3>
                </div>

                {/* 폐업지원금 신청 폭주 안내 및 상담 요청 폼 */}
                <div className="relative z-10 bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl p-5 md:p-6">
                  <div className="text-center mb-5">
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 md:p-4 mb-4">
                      <p className="text-sm md:text-base text-amber-800 font-medium leading-relaxed">
                        정부지원 폐업지원금은 소진시 종료되오니<br />빠른 상담 및 신청을 권장드립니다
                      </p>
                    </div>
                    <p className="text-lg md:text-xl text-gray-800 font-bold leading-relaxed">
                      아래 내용을 작성하시면<br />전문가가 연락을 드립니다.
                    </p>
                  </div>

                  {submitSuccess ? (
                    <div className="bg-white rounded-lg p-6 text-center">
                      <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                      </div>
                      <p className="text-lg font-bold text-gray-900 mb-2">상담 요청이 완료되었습니다!</p>
                      <p className="text-sm text-gray-600">빠른 시일 내에 연락드리겠습니다.</p>
                    </div>
                  ) : (
                    <form 
                      id="consult-form"
                      data-readdy-form
                      onSubmit={handleConsultSubmit}
                      className="bg-white rounded-lg p-5 md:p-6"
                    >
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">성함</label>
                          <input
                            type="text"
                            name="name"
                            value={consultName}
                            onChange={(e) => setConsultName(e.target.value)}
                            placeholder="성함을 입력해주세요"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">연락처</label>
                          <input
                            type="tel"
                            name="phone"
                            value={consultPhone}
                            onChange={(e) => setConsultPhone(e.target.value)}
                            placeholder="연락처를 입력해주세요"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {submitError && (
                        <p className="mt-3 text-sm text-red-600 text-center">{submitError}</p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="mt-5 w-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 text-white px-6 py-4 rounded-lg text-base font-bold hover:from-blue-600 hover:via-purple-600 hover:to-blue-600 transition-all shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i>
                            제출 중...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-check-line text-xl"></i>
                            상담 요청하기
                          </span>
                        )}
                      </button>
                    </form>
                  )}
                </div>

                <p className="mt-4 text-xs text-gray-500 text-center">
                  * 지원금은 지역·자격·기한에 따라 달라질 수 있습니다.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* 고정 CTA 버튼 (스크롤 따라다님) */}
        <div 
          className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${
            showFixedCTA ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="bg-white border-t-2 border-teal-600 shadow-2xl">
            <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-sm font-bold text-gray-900 truncate">
                    보증금 손실 막고 지원금 받으세요
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    오늘 {todayConsultations}명이 무료 진단 받았어요
                  </p>
                </div>
                <a
                  href="https://returnkorea.co.kr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 md:px-8 py-3 rounded-lg text-sm md:text-base font-bold hover:from-teal-700 hover:to-cyan-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                >
                  무료 진단 받기
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-teal-600 text-white py-10 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
              <div>
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">폐업지원금 간편 계산기</h4>
                <p className="text-xs md:text-sm text-teal-100 leading-relaxed">
                  간편하고 정확한 폐업지원금<br className="md:hidden" /> 계산 서비스를 제공합니다.
                </p>
              </div>
              
              <div>
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">문의하기</h4>
                <div className="space-y-2 text-xs md:text-sm text-teal-100">
                  <p className="flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>1555-6989</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>verum614@naver.com</span>
                  </p>
                </div>
              </div>
              
              <div>
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">회사 정보</h4>
                <div className="space-y-2 text-xs md:text-sm text-teal-100">
                  <p>주식회사 베룸</p>
                  <p>사업자번호: 260-86-03020</p>
                  <p>대표자: 현진호</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-teal-500 pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
              <p className="text-xs md:text-sm text-teal-100">
                © 2025 폐업지원금 계산기. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs md:text-sm text-teal-100 hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
